import ThemeToggle from '../shared/ThemeToggle';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import logo from '../../assets/logos/logo.png';
import { AlertTriangle, Building2, Cpu, LifeBuoy, Euro, History, LogOut, Menu, Network, X } from 'lucide-react';
import { supportAPI } from '../../services/supportApi';
import SupportStoresTab from './SupportStoresTab';
import SupportTicketsTab from './SupportTicketsTab';
import SupportAuditTab from './SupportAuditTab';
import SupportChainsTab from './SupportChainsTab';
import SupportFleetTab from './SupportFleetTab';
import SupportAttentionTab, { Attention } from './SupportAttentionTab';
import SuperAdminBillingTab from '../superadmin/SuperAdminBillingTab';

/**
 * Support panel — για τον ρόλο 'support' (υπάλληλοι HQ, κατάστημα 1).
 * Αδελφό shell του SuperAdminPanel με υποσύνολο: «Τι θέλει προσοχή» (πρώτη καρτέλα από 26/10/2026, ανανέωση ανά
 * λεπτό, ο αριθμός των κρίσιμων στην καρτέλα), καταστήματα (κάρτα καταστήματος, «Νέο κατάστημα» με
 * αλυσίδα από 25/10/2026, λίστα «Στήσιμο»), «Αλυσίδες» (καταστήματα + χρήστες οργανισμών), «Στόλος» (agent + εκτύπωση όλων), tickets, «Χρεώσεις» (από 27/10/2026
 * ό,τι και ο super admin ανά κατάστημα + «Μαζική συμπλήρωση»· η κάρτα καταστήματος ανοίγει εκεί) και «Ημερολόγιο»
 * ενεργειών support + super admin.
 * Χωρίς master access, χωρίς διαγραφή/επεξεργασία καταστήματος, χωρίς κατάλογο λειτουργιών/τιμών.
 */

type SupportTab = 'attention' | 'stores' | 'chains' | 'fleet' | 'tickets' | 'billing' | 'audit';

// «Άνοιγμα» από την «Προσοχή»: η κάρτα καταστήματος σε συγκεκριμένη καρτέλα (nonce = κάθε πάτημα ξανανοίγει).
export interface StoreFocus { storeId: number; tab: string; nonce: number }
const ATTENTION_REFRESH_MS = 60000;

const SupportPanel: React.FC = () => {
  const { logout, employee } = useAuth();
  const [activeTab, setActiveTab] = useState<SupportTab>('attention');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [attention, setAttention] = useState<Attention | null>(null);
  const [attentionFailed, setAttentionFailed] = useState(false);
  const [attentionLoading, setAttentionLoading] = useState(false);
  const [focus, setFocus] = useState<StoreFocus | null>(null);
  const [billingFocus, setBillingFocus] = useState<{ storeId: number; nonce: number } | null>(null);

  const loadAttention = useCallback(async () => {
    setAttentionLoading(true);
    try { setAttention(await supportAPI.getAttention() as unknown as Attention); setAttentionFailed(false); }
    catch { setAttentionFailed(true); } finally { setAttentionLoading(false); }
  }, []);
  // Ανανέωση κάθε λεπτό — μόνο όσο η σελίδα φαίνεται (καμία κίνηση σε κρυφή καρτέλα του browser).
  useEffect(() => {
    loadAttention();
    const timer = setInterval(() => { if (document.visibilityState === 'visible') loadAttention(); }, ATTENTION_REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadAttention]);
  const openStore = (storeId: number, tab: string) => { setFocus({ storeId, tab, nonce: Date.now() }); setActiveTab('stores'); };
  const openBilling = (storeId: number) => { setBillingFocus({ storeId, nonce: Date.now() }); setActiveTab('billing'); };
  const critical = attention?.summary.critical || 0;
  const warn = attention?.summary.warn || 0;
  const badge = (critical || warn) ? (
    <span data-testid="support-attention-badge" className={`min-w-[18px] px-1 rounded-full text-[11px] font-bold ${critical
      ? 'bg-red-500/15 text-red-700 dark:text-red-300' : 'bg-amber-500/15 text-amber-800 dark:text-amber-300'}`}>{critical || warn}</span>
  ) : null;

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const tabs: { id: SupportTab; label: string; icon: React.ElementType }[] = [
    { id: 'attention', label: 'Προσοχή', icon: AlertTriangle },
    { id: 'stores', label: 'Καταστήματα', icon: Building2 },
    { id: 'chains', label: 'Αλυσίδες', icon: Network },
    { id: 'fleet', label: 'Στόλος', icon: Cpu },
    { id: 'tickets', label: 'Αιτήματα', icon: LifeBuoy },
    { id: 'billing', label: 'Χρεώσεις', icon: Euro },
    { id: 'audit', label: 'Ημερολόγιο', icon: History },
  ];

  const activeItem = tabs.find(t => t.id === activeTab);
  const ActiveIcon = activeItem?.icon || Building2;

  return (
    <div className="min-h-screen bg-surface-0">
      {/* Header */}
      <nav className="role-header role-hq-header bg-surface-1 border-b border-line sticky top-0 z-50">
        <div className="w-full px-4">
          <div className="role-header-layout flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Xentrix Flow" className="h-8 w-auto" />
              <div>
                <span className="text-lg font-bold text-t1">Xentrix Flow</span>
                <p className="text-xs text-t3">Support</p>
              </div>
            </div>

            <div className="hidden md:flex items-center gap-1">
              {tabs.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button aria-current={isActive ? 'page' : undefined}
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setFocus(null); setBillingFocus(null); }}
                    className={`role-tab flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      isActive ? 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400' : 'text-t3 hover:text-t2 hover:bg-surface-3 dark:hover:bg-surface-2/50'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                    {tab.id === 'attention' && badge}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-3">
              <ThemeToggle />
              <div className="hidden md:block text-right">
                <p className="text-sm font-medium text-t1">
                  {employee?.first_name} {employee?.last_name}
                </p>
                <p className="text-xs text-t3">Υποστήριξη</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-t3 hover:text-t1 hover:bg-surface-2 rounded-lg transition-colors"
                title="Αποσύνδεση"
              >
                <LogOut className="h-5 w-5" />
              </button>
              <button onClick={() => setMobileOpen(true)} className="md:hidden p-2 text-t3 hover:text-t1">
                <Menu className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="role-overlay fixed inset-0 bg-black/60 z-50" onClick={() => setMobileOpen(false)} />
          <div className="fixed top-0 right-0 w-72 h-full bg-surface-1 border-l border-line z-50 p-4">
            <div className="flex justify-between items-center mb-6">
              <span className="text-lg font-bold text-t1">Menu</span>
              <button onClick={() => setMobileOpen(false)} className="p-2 text-t3 hover:text-t1">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-1">
              {tabs.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button aria-current={isActive ? 'page' : undefined}
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setFocus(null); setBillingFocus(null); setMobileOpen(false); }}
                    className={`role-tab w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      isActive ? 'bg-cyan-500/20 text-cyan-600 dark:text-cyan-400' : 'text-t3 hover:text-t1 hover:bg-surface-2'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    {tab.label}
                    {tab.id === 'attention' && badge}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Mobile active tab label */}
      <div className="md:hidden sticky top-16 z-40 bg-surface-1/95 backdrop-blur border-b border-line">
        <div className="flex items-center gap-2 px-4 py-2">
          <ActiveIcon className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
          <span className="text-sm font-medium text-cyan-600 dark:text-cyan-400">{activeItem?.label}</span>
        </div>
      </div>

      {/* Content */}
      <div className="w-full">
        {activeTab === 'attention' && (
          <SupportAttentionTab data={attention} failed={attentionFailed} loading={attentionLoading} onRefresh={loadAttention} onOpen={openStore} />
        )}
        {activeTab === 'stores' && <SupportStoresTab focus={focus} onOpenBilling={openBilling} />}
        {activeTab === 'chains' && <SupportChainsTab />}
        {activeTab === 'fleet' && <SupportFleetTab />}
        {activeTab === 'tickets' && <SupportTicketsTab />}
        {activeTab === 'billing' && <SuperAdminBillingTab support focusStore={billingFocus} />}
        {activeTab === 'audit' && <SupportAuditTab />}
      </div>
    </div>
  );
};

export default SupportPanel;
